import React, { useEffect } from 'react'
import { create } from 'zustand'

// Initial state (same as before)
const initialState = {
  user: null,
  company: null,
  subLhs: [],
  loadAllLhs: [],
  lhs: [],
  isLoading: true,
  isAuthenticated: false,
  isCSVModalActive: false,
  isPPTModalActive: false,
  isPPTModalActiveAndClicked: false,
  isAskIaActiveState: 'none',
  isSnapshotActiveState: 'none',
  subLhsApiLoaded: {},
  projectIdValue: {},
  csvSubLhs: [],
  exportToCsvObject: {},
  exportToCsvCount: 0,
  pptSubLhs: [],
  pptSubLhsAppendices: [],
  pptSubLhsProgression: [],
  pptIdsType: '',
  exportToPptObject: {},
  isTourGuideActive: false,
  showLhsMenu: 'projectPage',
  tourGuideData: [],
  driverProgressionPopupObject: {},
  isFullScreen: false,
  theme: 'light',
  resizeWidth: typeof window !== 'undefined' ? window.innerWidth : 1024,
  hasSnapshotData: null,
  currentTabId: null,
  isAskIAModalActive: false,
  isSnapshotModalActive: false
}

// Create the Zustand store
const useZustandStore = create((set, get) => ({
  // State
  ...initialState,

  // Actions (keeping the same method names as your original)
  setUser: (user) => {
    set({
      user,
      isLoading: false,
      isAuthenticated: !!user?.UserId
    })
  },

  setCompany: (company) => {
    set({
      company,
      isLoading: false,
      isAuthenticated: !!company?.CompanyId
    })
  },

  setLhs: (lhs) => set({ lhs }),

  setSubLhs: (subLhs) => set({ subLhs }),

  setLoadAllLhs: (loadAllLhs) => set({ loadAllLhs }),

  setCSVModalActive: (payload) => set({ isCSVModalActive: payload }),

  setPPTModalActive: (payload) => set({ isPPTModalActive: payload }),

  setPPTModalActiveAndClicked: (payload) => set({ isPPTModalActiveAndClicked: payload }),

  setAskIaActiveState: (payload) => set({ isAskIaActiveState: payload }),

  setSnapshotActiveState: (payload) => set({ isSnapshotActiveState: payload }),

  setCSVSubLhs: (payload) => set({ csvSubLhs: payload }),

  setExportToCsvObject: (param) => {
    const { exportToCsvObject } = get()
    set({ exportToCsvObject: { ...exportToCsvObject, ...param } })
  },

  setExportToCsvCount: (param) => set({ exportToCsvCount: param }),

  setPPTSubLhs: (subLhs) => set({ pptSubLhs: subLhs }),

  setPPTSubLhsAppendices: (param) => set({ pptSubLhsAppendices: param }),

  setPPTSubLhsProgression: (param) => set({ pptSubLhsProgression: param }),

  setPptIdsType: (param) => set({ pptIdsType: param }),

  setExportToPptObject: (param) => {
    const { exportToPptObject } = get()
    set({ exportToPptObject: { ...exportToPptObject, ...param } })
  },

  setTourGuideActive: (param) => set({ isTourGuideActive: param }),

  setTourGuideData: (param) => set({ tourGuideData: param }),

  setSubLhsApiLoaded: (param) => {
    const { subLhsApiLoaded } = get()
    set({ subLhsApiLoaded: { ...subLhsApiLoaded, ...param } })
  },

  setProjectIdValue: (param) => {
    const { projectIdValue } = get()
    set({ projectIdValue: { ...projectIdValue, ...param } })
  },

  setShowLhsMenu: (param) => set({ showLhsMenu: param }),

  setDriverProgressionPopupObject: (param) => set({ driverProgressionPopupObject: param }),

  setFullScreen: (param) => set({ isFullScreen: param }),

  setTheme: (param) => set({ theme: param }),

  setAskIAModalActive: (param) => set({ isAskIAModalActive: param }),

  setResizeWidth: (param) => set({ resizeWidth: param }),

  setSnapshotModalActive: (param) => set({ isSnapshotModalActive: param }),

  setSnapshotData: (param) => set({ hasSnapshotData: param }),
  
  setCurrentTabId: (param) => set({ currentTabId: param }),

  // Initialize method to handle localStorage loading
  initialize: () => {
    if (typeof window !== 'undefined') {
      const user = JSON.parse(localStorage.getItem('user') || 'null')
      const company = JSON.parse(localStorage.getItem('company') || 'null')

      set({
        user,
        company,
        isLoading: false,
        isAuthenticated: !!(user?.UserId || company?.CompanyId)
      })
    }
  }
}))

// Legacy context (keep for compatibility)
export const TodoListContext = React.createContext()

// Provider component that initializes the store
const Provider = ({ children }) => {
  const initialize = useZustandStore(state => state.initialize)

  useEffect(() => {
    initialize()
  }, [initialize])

  // Keep the context wrapper for any legacy dependencies
  return <TodoListContext.Provider value={null}>{children}</TodoListContext.Provider>
}

// The main hook that your components will use - keeping the exact same interface
export const useGlobalStore = () => {
  // Get all state and actions from Zustand store
  const store = useZustandStore()

  // Return the same interface as before - your components won't know the difference!
  return {
    // State properties
    user: store.user,
    company: store.company,
    subLhs: store.subLhs,
    loadAllLhs: store.loadAllLhs,
    lhs: store.lhs,
    isLoading: store.isLoading,
    isAuthenticated: store.isAuthenticated,
    isCSVModalActive: store.isCSVModalActive,
    isPPTModalActive: store.isPPTModalActive,
    isPPTModalActiveAndClicked: store.isPPTModalActiveAndClicked,
    isAskIaActiveState: store.isAskIaActiveState,
    isSnapshotActiveState: store.isSnapshotActiveState,
    subLhsApiLoaded: store.subLhsApiLoaded,
    projectIdValue: store.projectIdValue,
    csvSubLhs: store.csvSubLhs,
    exportToCsvObject: store.exportToCsvObject,
    exportToCsvCount: store.exportToCsvCount,
    pptSubLhs: store.pptSubLhs,
    pptSubLhsAppendices: store.pptSubLhsAppendices,
    pptSubLhsProgression: store.pptSubLhsProgression,
    pptIdsType: store.pptIdsType,
    exportToPptObject: store.exportToPptObject,
    isTourGuideActive: store.isTourGuideActive,
    tourGuideData: store.tourGuideData,
    showLhsMenu: store.showLhsMenu,
    driverProgressionPopupObject: store.driverProgressionPopupObject,
    isFullScreen: store.isFullScreen,
    theme: store.theme,
    isAskIAModalActive: store.isAskIAModalActive,
    resizeWidth: store.resizeWidth,
    isSnapshotModalActive: store.isSnapshotModalActive,
    hasSnapshotData: store.hasSnapshotData,
    currentTabId: store.currentTabId,

    // Action methods (same names as before)
    setUser: store.setUser,
    setCompany: store.setCompany,
    setLhs: store.setLhs,
    setSubLhs: store.setSubLhs,
    setLoadAllLhs: store.setLoadAllLhs,
    setCSVModalActive: store.setCSVModalActive,
    setPPTModalActive: store.setPPTModalActive,
    setPPTModalActiveAndClicked: store.setPPTModalActiveAndClicked,
    setAskIaActiveState: store.setAskIaActiveState,
    setSnapshotActiveState: store.setSnapshotActiveState,
    setCSVSubLhs: store.setCSVSubLhs,
    setExportToCsvObject: store.setExportToCsvObject,
    setExportToCsvCount: store.setExportToCsvCount,
    setPPTSubLhs: store.setPPTSubLhs,
    setPPTSubLhsAppendices: store.setPPTSubLhsAppendices,
    setPPTSubLhsProgression: store.setPPTSubLhsProgression,
    setPptIdsType: store.setPptIdsType,
    setExportToPptObject: store.setExportToPptObject,
    setTourGuideActive: store.setTourGuideActive,
    setTourGuideData: store.setTourGuideData,
    setSubLhsApiLoaded: store.setSubLhsApiLoaded,
    setProjectIdValue: store.setProjectIdValue,
    setShowLhsMenu: store.setShowLhsMenu,
    setDriverProgressionPopupObject: store.setDriverProgressionPopupObject,
    setFullScreen: store.setFullScreen,
    setTheme: store.setTheme,
    setAskIAModalActive: store.setAskIAModalActive,
    setResizeWidth: store.setResizeWidth,
    setSnapshotModalActive: store.setSnapshotModalActive,
    setSnapshotData: store.setSnapshotData,
    setCurrentTabId: store.setCurrentTabId,
  }
}

export default Provider